import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="media-modal"
export default class extends Controller {
  static targets = ["content", "modal"]
  static values = { videoFallback: String, loading: String }

  connect() {
    this._onModalShow = this._onModalShow.bind(this)
    this._onModalHidden = this._onModalHidden.bind(this)

    // If Stimulus targets are present, use them. Otherwise fallback to querying by id.
    this._ensureModalElements()
    if (this._modalElement) {
      // Listen to show event so we can read event.relatedTarget (the trigger that opened the modal)
      this._modalElement.addEventListener('show.bs.modal', this._onModalShow)
      this._modalElement.addEventListener('hidden.bs.modal', this._onModalHidden)
    }
  }

  disconnect() {
    if (this._modalElement) {
      this._modalElement.removeEventListener('show.bs.modal', this._onModalShow)
      this._modalElement.removeEventListener('hidden.bs.modal', this._onModalHidden)
    }
  }

  _ensureModalElements() {
    // Prefer Stimulus targets when available
    this._contentElement = this.hasContentTarget ? this.contentTarget : document.getElementById('modalMediaContent')
    this._modalElement = this.hasModalTarget ? this.modalTarget : document.getElementById('mediaModal')
  }

  _onModalShow(e) {
    const trigger = e.relatedTarget
    if (!trigger) return

    const mediaUrl = trigger.dataset.mediaUrl
    const mediaType = trigger.dataset.mediaType
    if (!mediaUrl || !mediaType) return

    this._ensureModalElements()
    if (!this._contentElement) return

    // Show loading spinner until media is ready
    const loadingText = this.hasLoadingValue ? this.loadingValue : 'Loading...'
    const spinnerHtml = `
      <div class="spinner-border text-primary mb-3" role="status">
        <span class="visually-hidden">${this._escapeHtml(loadingText)}</span>
      </div>
    `

    // Reset content to spinner and we'll append media element below
    this._contentElement.innerHTML = spinnerHtml

    if (mediaType === 'image') {
      // Create image and show spinner until it loads
      const img = document.createElement('img')
      img.className = 'img-fluid d-none rounded'
      img.alt = loadingText

      const onLoad = () => {
        // remove spinner and show image
        const spinner = this._contentElement.querySelector('.spinner-border')
        if (spinner) spinner.remove()
        img.classList.remove('d-none')
        img.removeEventListener('load', onLoad)
        img.removeEventListener('error', onError)
      }

      const onError = () => {
        const spinner = this._contentElement.querySelector('.spinner-border')
        if (spinner) spinner.remove()
        this._contentElement.innerHTML = `<div class=\"text-danger\">${this._escapeHtml(this.hasVideoFallbackValue ? this.videoFallbackValue : 'Failed to load media')}</div>`
      }

      img.addEventListener('load', onLoad)
      img.addEventListener('error', onError)
      img.src = mediaUrl
      this._contentElement.appendChild(img)
    } else if (mediaType === 'video') {
      const fallback = this.hasVideoFallbackValue ? this.videoFallbackValue : 'Your browser does not support the video tag.'
      // Create video element and show spinner until first playback data is available
      const video = document.createElement('video')
      video.className = 'img-fluid d-none'
      video.controls = true
      video.autoplay = true
      video.preload = 'metadata'

      const source = document.createElement('source')
      source.src = mediaUrl
      source.type = ''
      video.appendChild(source)

      const onLoadedData = () => {
        const spinner = this._contentElement.querySelector('.spinner-border')
        if (spinner) spinner.remove()
        video.classList.remove('d-none')
        video.removeEventListener('loadeddata', onLoadedData)
        video.removeEventListener('error', onError)
      }

      const onError = () => {
        const spinner = this._contentElement.querySelector('.spinner-border')
        if (spinner) spinner.remove()
        this._contentElement.innerHTML = `<div class=\"text-danger\">${this._escapeHtml(fallback)}</div>`
      }

      video.addEventListener('loadeddata', onLoadedData)
      video.addEventListener('error', onError)

      this._contentElement.appendChild(video)
    }
  }

  _onModalHidden() {
    this._ensureModalElements()
    if (this._contentElement) {
      this._contentElement.innerHTML = ''
    }
  }

  // Minimal escaping for fallback strings
  _escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
  }
}
